import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { CONTENT } from '../content';
import { TokenomicsD } from './Tokenomics';
import { ComicShelf } from '../comic/ComicReader';
import { Fireflies } from './Fireflies';
import { RoadmapRail } from './RoadmapRail';
import { shareMeme } from '../m2/share';
import {
  AboutCopy,
  ArtistCopy,
  Buy,
  C,
  COMIC_BOOK,
  ContractAddress,
  CtoCopy,
  DrawnFooter,
  DrawnNav,
  EASE_OUT,
  InkRing,
  InkUnderline,
  MEMES,
  NDHAUS_PHOTO,
  ORIGINAL_ART,
  Fold,
  ROADMAP,
  RoadmapStop,
  RedesignFonts,
  Socials,
  Sparkle,
  TITLES,
  TitleArt,
  VIDEOS,
  Video,
  hover,
  BuyButton,
  Lettering,
  NAV,
} from '../kit';

/**
 * D, "the Boy's Club house and the swamp". One continuous drawing read top to bottom: the
 * page opens on the roommates' house with Zeus on the lawn, tells the story on the sky,
 * runs the 3D Zeus film behind the community chapter, and lands in the swamp, where the
 * memes float on the water. Text sits on the art itself; the only lines are ink strokes.
 */

/**
 * The road shows where Zeus started and where the community is now: the first two and the
 * last two milestones stay open, everything in between folds into one control.
 */
const HEAD = ROADMAP.slice(0, 2);
const TAIL = ROADMAP.slice(-2);
const MIDDLE = ROADMAP.slice(2, -2);
const dateOf = (stop: RoadmapStop) => stop.lines.join(' ').split(':')[0].trim();
const MIDDLE_SPAN = `${dateOf(MIDDLE[0])} to ${dateOf(MIDDLE[MIDDLE.length - 1])}`;

/** Referral gives way to the dollarize chapter in this design's menu. */
const D_NAV = NAV.map((item) =>
  item.label === 'REFERRAL' ? { label: 'DOLLARIZE', href: '#dollarize' } : item
);

const renderStop = ({ milestone, lines, pill }: RoadmapStop, i: number) => (
  <Stop key={milestone.id} $side={i % 2 === 0 ? 'left' : 'right'} $status={milestone.status}>
    <span className="dot" aria-hidden="true" />
    <div className="what">
      {pill && <span className="pill">{pill}</span>}
      <h3>
        {lines.map((l) => (
          <span key={l}>{l}</span>
        ))}
      </h3>
      <p>{milestone.description}</p>
      {milestone.link && (
        <a href={milestone.link.href} target="_blank" rel="noopener noreferrer">
          {milestone.link.label}
        </a>
      )}
    </div>
  </Stop>
);

/** The header lands on the water: a drawn pond edge with reeds, lily pads and a lotus. */
const REEDS = [40, 70, 205, 1190, 1235, 1270, 1395];
const PADS: Array<[number, number, number]> = [
  [150, 92, 1], [330, 104, 0.8], [520, 88, 1.1], [760, 100, 0.9], [980, 90, 1], [1120, 106, 0.75], [1330, 96, 1.05],
];

const PondEdge: React.FC = () => (
  <Pond aria-hidden="true">
    <svg viewBox="0 0 1440 180" preserveAspectRatio="xMidYMax slice">
      {REEDS.map((x, i) => (
        <g key={x} className="reed" style={{ animationDelay: `${i * -0.7}s`, transformOrigin: `${x}px 90px` }}>
          <path d={`M${x} 92 C ${x + 4} 60, ${x - 2} 30, ${x + 6} ${8 + (i % 3) * 8}`} fill="none" stroke={C.ink} strokeWidth="4" strokeLinecap="round" />
          <path d={`M${x} 92 C ${x + 2} 70, ${x + 8} 52, ${x + 22} 40`} fill="none" stroke="#2e8b3e" strokeWidth="5" strokeLinecap="round" />
          <rect x={x - 3} y={14 + (i % 3) * 8} width="16" height="34" rx="8" fill="#7a4a24" stroke={C.ink} strokeWidth="3" />
        </g>
      ))}
      <path
        d="M0 84 C 120 70, 220 96, 360 82 S 620 70, 760 86 S 1040 98, 1180 80 S 1360 74, 1440 86 L 1440 180 L 0 180 Z"
        fill="#2f86e8"
        stroke={C.ink}
        strokeWidth="4"
      />
      <path d="M60 120 C 120 112, 180 126, 240 118 M620 132 C 700 122, 760 138, 840 128 M1040 120 C 1100 112, 1160 124, 1230 116" fill="none" stroke="#8fd0ff" strokeWidth="4" strokeLinecap="round" />
      {PADS.map(([x, y, k]) => (
        <g key={x} transform={`translate(${x} ${y}) scale(${k})`} className="pad">
          <path d="M0 0 C -40 -2, -52 18, -20 22 C 10 26, 52 20, 44 4 C 40 -6, 20 -8, 6 -4 L 0 8 Z" fill="#3bb54a" stroke={C.ink} strokeWidth="3" strokeLinejoin="round" />
          <path d="M0 8 L -22 12 M0 8 L 24 12" stroke="#2e8b3e" strokeWidth="2.5" strokeLinecap="round" />
        </g>
      ))}
      <g transform="translate(860 86)">
        <path d="M0 0 C -14 -6, -20 -26, -8 -34 C -4 -20, 0 -14, 0 0 Z M0 0 C 14 -6, 20 -26, 8 -34 C 4 -20, 0 -14, 0 0 Z M0 0 C -6 -14, -4 -34, 0 -42 C 4 -34, 6 -14, 0 0 Z" fill="#f45db3" stroke={C.ink} strokeWidth="3" strokeLinejoin="round" />
      </g>
      <path d="M0 150 C 240 136, 520 166, 760 150 S 1220 138, 1440 152 L 1440 180 L 0 180 Z" fill={C.light} />
    </svg>
  </Pond>
);

/** Dock planks continuing the dock that closes the golden hour header. */
const DockEdge: React.FC = () => (
  <Edge $lift={70} aria-hidden="true">
    <svg viewBox="0 0 1440 150" preserveAspectRatio="xMidYMax slice">
      <path d="M0 22 C 240 14, 480 30, 720 20 S 1200 12, 1440 24 L 1440 96 L 0 96 Z" fill="#a8672f" stroke={C.ink} strokeWidth="4" />
      {Array.from({ length: 19 }, (_, i) => (
        <g key={i}>
          <path d={`M${i * 80 + 40} ${20 + (i % 3) * 2} L ${i * 80 + 36} 96`} stroke={C.ink} strokeWidth="3" />
          <circle cx={i * 80 + 30} cy="34" r="2.5" fill={C.ink} />
          <circle cx={i * 80 + 30} cy="80" r="2.5" fill={C.ink} />
        </g>
      ))}
      <path d="M0 52 C 300 46, 700 60, 1440 50" fill="none" stroke="#7a4a24" strokeWidth="3" />
      {[120, 520, 980, 1300].map((x) => (
        <path key={x} d={`M${x} 96 L ${x} 128 M${x + 22} 96 L ${x + 22} 120`} stroke="#7a4a24" strokeWidth="12" strokeLinecap="round" />
      ))}
      <path d="M0 118 C 260 104, 560 134, 860 116 S 1300 106, 1440 120 L 1440 150 L 0 150 Z" fill={C.light} />
    </svg>
  </Edge>
);

/** Layered water with bubbles, for the pool party header. */
const WaterEdge: React.FC = () => (
  <Edge $lift={80} aria-hidden="true">
    <svg viewBox="0 0 1440 170" preserveAspectRatio="xMidYMax slice">
      <path className="w1" d="M0 40 C 120 20, 240 60, 360 40 S 600 20, 720 40 S 960 60, 1080 40 S 1320 20, 1440 40 L 1440 170 L 0 170 Z" fill="#1f73d6" stroke={C.ink} strokeWidth="4" />
      <path className="w2" d="M0 78 C 140 60, 260 96, 400 78 S 660 60, 800 80 S 1080 96, 1220 78 S 1400 66, 1440 76 L 1440 170 L 0 170 Z" fill="#3d8df5" />
      <path className="w3" d="M0 112 C 160 98, 300 128, 460 112 S 760 96, 920 114 S 1240 126, 1440 110 L 1440 170 L 0 170 Z" fill={C.light} />
      {[[90, 64, 9], [230, 92, 6], [610, 58, 8], [700, 96, 5], [1010, 70, 10], [1180, 94, 6], [1350, 60, 7]].map(([x, y, r]) => (
        <circle key={x} className="bubble" cx={x} cy={y} r={r} fill="none" stroke={C.white} strokeWidth="3" />
      ))}
    </svg>
  </Edge>
);

/** Night grass with fireflies, for the campfire header. */
const GrassEdge: React.FC = () => (
  <Edge $lift={86} aria-hidden="true">
    <svg viewBox="0 0 1440 170" preserveAspectRatio="xMidYMax slice">
      <path
        d={`M0 170 L0 70 ${Array.from({ length: 48 }, (_, i) => {
          const x = i * 30;
          return `L ${x + 8} ${40 + ((i * 37) % 30)} L ${x + 15} 78 L ${x + 23} ${30 + ((i * 53) % 40)} L ${x + 30} 74`;
        }).join(' ')} L 1440 170 Z`}
        fill="#16402a"
      />
      <path d="M0 126 C 260 110, 560 140, 860 122 S 1300 112, 1440 126 L 1440 170 L 0 170 Z" fill={C.light} />
      {[[80, 30], [260, 18], [470, 40], [690, 22], [900, 36], [1110, 16], [1330, 34]].map(([x, y], i) => (
        <circle key={x} className="fly" cx={x} cy={y} r="5" fill="#ffe66b" style={{ animationDelay: `${i * -0.5}s` }} />
      ))}
    </svg>
  </Edge>
);

type HeaderKey = 'house' | 'dock' | 'pool' | 'camp';

const HEADERS: Record<HeaderKey, { src: string; sky: string; position: string; Edge: React.FC }> = {
  house: { src: '/redesign2/scenes/house.webp', sky: '#3BAAFD', position: '70% 30%', Edge: () => <PondEdge /> },
  dock: { src: '/redesign2/scenes/dock.webp', sky: '#61ABFD', position: '70% 40%', Edge: DockEdge },
  pool: { src: '/redesign2/scenes/pool.webp', sky: '#3CB1FC', position: '70% 40%', Edge: WaterEdge },
  camp: { src: '/redesign2/scenes/camp.webp', sky: '#011A4A', position: '70% 40%', Edge: GrassEdge },
};

/** Local review only: `?header=dock|pool|camp` swaps the header to compare options. */
const pickHeader = (): HeaderKey => {
  if (typeof window === 'undefined') return 'camp';
  const key = new URLSearchParams(window.location.search).get('header');
  return key && key in HEADERS ? (key as HeaderKey) : 'camp';
};

const RedesignD: React.FC = () => {
  const header = HEADERS[pickHeader()];
  return (
  <Page data-design="d" id="top">
    <RedesignFonts />

    <Header style={{ background: header.sky }}>
      <img className="scene" src={header.src} alt="" aria-hidden="true" style={{ objectPosition: header.position }} />
      {header === HEADERS.camp && <Fireflies />}
      <div className="over">
        <DrawnNav color={C.white} items={D_NAV} />
        <div className="copy">
          <h1>
            <TitleArt src={TITLES.zeus.src} alt={TITLES.zeus.alt} $w="min(470px, 84vw)" $tilt={-3} />
          </h1>
          <ContractAddress tone="dark" />
          <div className="actions">
            <Buy big />
          </div>
        </div>
      </div>
    </Header>

    <header.Edge />

    <Story>
      <Split id="about">
        <div className="text">
          <h2>
            <TitleArt src={TITLES.pepesDog.src} alt={TITLES.pepesDog.alt} $w="min(290px, 62vw)" />
          </h2>
          <Prose>
            <AboutCopy />
          </Prose>
        </div>
        <ArtWithRing>
          <img src={ORIGINAL_ART} alt="The Happiness of Zeus, the original artwork by ndhaus" />
          <InkRing className="ring" />
        </ArtWithRing>
      </Split>

      <Screen>
        <Sparkle className="b1" size={54} color={C.yellow} />
        <Video video={VIDEOS.trailer} className="video" />
        <Sparkle className="b2" size={40} color={C.pink} />
      </Screen>
    </Story>

    <TakeOver>
      <video
        className="bg"
        src={VIDEOS.buy.src}
        poster={VIDEOS.buy.poster}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        aria-hidden="true"
      />
      <div className="text">
        <h2>
          <TitleArt src={TITLES.takeOver.src} alt={TITLES.takeOver.alt} $w="min(380px, 76vw)" $tilt={2} />
        </h2>
        <Prose>
          <CtoCopy />
        </Prose>
      </div>
    </TakeOver>

    <Descent>
      <Block id="tokenomics">
        <h2 className="center">
          <TitleArt src={TITLES.tokenomics.src} alt={TITLES.tokenomics.alt} $w="min(480px, 92vw, 42vh)" />
        </h2>
        <TokenomicsD />
      </Block>

      <Dollarize id="dollarize">
        <img
          data-section-bg
          className="bg"
          src="/redesign2/scenes/blackhole-zeus.webp"
          alt="Zeus sitting on a lily pad at the edge of a golden black hole that swallows coins and bills, the gang watching from the reeds"
          loading="lazy"
        />
        <span className="swirl" aria-hidden="true" />
        <div className="inner">
          <div className="bubble" data-bubble>
            <h2>
              <Lettering $size="clamp(30px, min(3.6vw, 6vh), 54px)" $tilt={-2}>
                {CONTENT.dollarize.title}
              </Lettering>
            </h2>
            {CONTENT.dollarize.paragraphs.map((p) => (
              <p key={p.slice(0, 24)}>{p}</p>
            ))}
            <div className="links">
              <a className="mint" href={CONTENT.dollarize.mintLink} target="_blank" rel="noopener noreferrer">
                <span className="coin" aria-hidden="true">$</span>
                MINT USDZEUS
              </a>
              <a className="how" href={CONTENT.dollarize.howLink} target="_blank" rel="noopener noreferrer">
                How the black hole works
              </a>
            </div>
          </div>
        </div>
      </Dollarize>

      <Block id="roadmap">
        <h2 className="center">
          <TitleArt src={TITLES.roadmap.src} alt={TITLES.roadmap.alt} $w="min(460px, 86vw, 44vh)" />
        </h2>
        <RoadmapRail />
      </Block>


      <Split id="artist">
        <div className="text">
          <h2>
            <TitleArt src={TITLES.artist.src} alt={TITLES.artist.alt} $w="min(330px, 70vw)" />
          </h2>
          <Prose>
            <ArtistCopy />
          </Prose>
        </div>
        <ArtWithRing $tilt={3}>
          <img src={NDHAUS_PHOTO} alt="ndhaus holding the original Zeus artwork" />
          <InkRing className="ring" color={C.yellow} />
        </ArtWithRing>
      </Split>
      <Nfts id="nfts">
        <div className="head">
          <h2>
            <Lettering $size="clamp(30px, 3.6vw, 52px)" $tilt={2}>
              {CONTENT.nfts.title}
            </Lettering>
          </h2>
          <Prose>
            {CONTENT.nfts.paragraphs.map((p) => (
              <p key={p.slice(0, 24)}>{p}</p>
            ))}
          </Prose>
          <a className="how ink-host" href={CONTENT.nfts.link} target="_blank" rel="noopener noreferrer">
            See the collection on OpenSea
            <InkUnderline color={C.yellow} />
          </a>
        </div>
        <ul className="frames">
          {CONTENT.nfts.items.map((id, i) => (
            <li key={id} style={{ transform: `rotate(${[-4, 3, -2, 4, -3, 2, -4, 3, -2, 4][i]}deg)` }}>
              <a href={`https://opensea.io/item/ethereum/0xe6020fc2913a096dffa1a25a649bb3ef3d2447e1/${id}`} target="_blank" rel="noopener noreferrer">
                <img src={`/redesign2/nfts/${id}.webp`} alt={`Dreamscape of Zeus #${id} by ndhaus`} loading="lazy" />
              </a>
            </li>
          ))}
        </ul>
      </Nfts>
    </Descent>

    <Swamp>
      <h2 className="center memes-title">
        <TitleArt src={TITLES.memes.src} alt={TITLES.memes.alt} $w="min(380px, 70vw)" />
      </h2>
      <div className="stage">
        <img className="scene" src="/redesign2/scenes/swamp-empty.webp" alt="" aria-hidden="true" />
        <ul className="pads">
          {MEMES.map((m, i) => (
            <li key={m.src} style={{ animationDelay: `${i * -0.4}s` }}>
              <div className="bub">
                <img src={m.src} alt={m.alt} loading="lazy" title="Share on X" onClick={() => shareMeme(i, m.alt)} />
                <button type="button" className="share-x" aria-label={`Share "${m.alt}" on X`} onClick={() => shareMeme(i, m.alt)}>
                  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                    <path fill="currentColor" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </Swamp>

    <Shore>
      <Block>
        <h2 className="center">
          <TitleArt src={TITLES.comic.src} alt={TITLES.comic.alt} $w="min(380px, 76vw)" />
        </h2>
        <p className="center sub">{CONTENT.comic.subtitle}</p>
        <ComicShelf pages={COMIC_BOOK} preview={[0, 1, 2]} pdf="/zeus.pdf" />
      </Block>

      <Join>
        <img className="zeus" src="/redesign2/scenes/gang.webp" alt="Zeus celebrating with Pepe, Brett, Andy and Landwolf" loading="lazy" />
        <div>
          <Buy big />
          <Socials color={C.ink} size={30} />
        </div>
      </Join>
      <DrawnFooter color={C.ink} />
    </Shore>
  </Page>
  );
};

export default RedesignD;

/* --------------------------------------------------------------------------- */

const Page = styled.div`
  font-family: 'Ubuntu', sans-serif;
  color: ${C.ink};
  background: ${C.blue};
  .center {
    display: flex;
    justify-content: center;
    text-align: center;
  }
  h2 {
    margin: 0 0 28px;
  }
`;

const SKY = '#3BAAFD';

const Header = styled.header`
  position: relative;
  background: ${SKY};
  .scene {
    display: block;
    width: 100%;
    height: min(calc(100vw * 941 / 1672), calc(100vh - 170px));
    object-fit: cover;
    min-height: 620px;
    max-height: 100vh;
    object-fit: cover;
    object-position: 70% 30%;
  }
  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    z-index: 1;
    height: clamp(150px, 13vw, 260px);
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0.28) 45%, rgba(0, 0, 0, 0) 100%);
    pointer-events: none;
  }
  .over {
    position: absolute;
    inset: 0 0 auto 0;
    z-index: 2;
  }
  .copy {
    max-width: 1320px;
    margin: 0 auto;
    padding: clamp(0px, 1vh, 16px) clamp(16px, 4vw, 40px) 0;
  }
  h1 {
    margin: 0 0 30px -10px;
  }
  .actions {
    margin-top: 28px;
  }
  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    .over {
      position: static;
      order: -1;
    }
    .scene {
      min-height: 0;
      height: 320px;
      object-position: 78% 50%;
    }
    .copy {
      text-align: center;
      padding-bottom: 10px;
    }
    h1 {
      display: flex;
      justify-content: center;
      margin: 0 0 18px;
    }
  }
`;

const sway = keyframes`
  from { transform: rotate(-2deg); }
  to { transform: rotate(2.5deg); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); opacity: 0.9; }
  50% { transform: translateY(-8px); opacity: 0.4; }
`;

const Edge = styled.div<{ $lift: number }>`
  position: relative;
  z-index: 2;
  margin-top: -${(p) => p.$lift}px;
  margin-bottom: -2px;
  line-height: 0;
  svg {
    display: block;
    width: 100%;
    height: clamp(80px, 11vw, 170px);
    overflow: hidden;
  }
  .bubble,
  .fly {
    animation: ${float} 3.2s ease-in-out infinite;
  }
  .fly {
    filter: drop-shadow(0 0 6px #ffe66b);
  }
  @media (max-width: 900px) {
    margin-top: -${(p) => Math.round(p.$lift / 2)}px;
  }
`;

const Pond = styled.div`
  position: relative;
  z-index: 2;
  margin-top: -96px;
  margin-bottom: -2px;
  line-height: 0;
  svg {
    display: block;
    width: 100%;
    height: clamp(90px, 12vw, 180px);
    overflow: visible;
  }
  .reed {
    animation: ${sway} 3.6s ease-in-out infinite alternate;
  }
  .pad {
    transition: transform 300ms ${EASE_OUT};
  }
  @media (max-width: 900px) {
    margin-top: -50px;
  }
`;

const TakeOver = styled.section`
  position: relative;
  min-height: min(860px, 100vh);
  max-height: 100vh;
  display: flex;
  align-items: center;
  overflow: hidden;
  background: ${C.light};
  .bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    -webkit-mask-image: linear-gradient(180deg, transparent 0%, #000 14%, #000 86%, transparent 100%);
    mask-image: linear-gradient(180deg, transparent 0%, #000 14%, #000 86%, transparent 100%);
  }
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, rgba(0, 0, 0, 0.62) 0%, rgba(0, 0, 0, 0.38) 38%, rgba(0, 0, 0, 0) 72%);
    -webkit-mask-image: linear-gradient(180deg, transparent 0%, #000 14%, #000 86%, transparent 100%);
    mask-image: linear-gradient(180deg, transparent 0%, #000 14%, #000 86%, transparent 100%);
    pointer-events: none;
  }
  .text {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 1240px;
    margin: 0 auto;
    padding: clamp(40px, 12vh, 120px) clamp(16px, 4vw, 40px);
    color: ${C.white};
  }
  .text > div {
    max-width: 52ch;
    text-shadow: 0 1px 12px rgba(0, 0, 0, 0.35);
  }
  @media (max-width: 900px) {
    &::after {
      background: linear-gradient(90deg, rgba(0, 0, 0, 0.62), rgba(0, 0, 0, 0.42));
    }
  }
`;

const PRINT = 'radial-gradient(rgba(255, 255, 255, 0.35) 1.5px, transparent 1.7px)';

const Story = styled.div`
  position: relative;
  background-color: ${C.light};
  background-image: ${PRINT};
  background-size: 18px 18px;
  padding: 30px 0 60px;
`;

const Split = styled.section<{ $reverse?: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
  align-items: center;
  gap: clamp(28px, 5vw, 80px);
  max-width: 1240px;
  margin: 0 auto;
  padding: clamp(24px, 5vh, 90px) clamp(16px, 4vw, 40px);
  ${(p) =>
    p.$reverse &&
    css`
      grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
      .text {
        order: 2;
      }
    `}
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    .text {
      order: 0;
    }
  }
`;

const Prose = styled.div`
  max-width: 60ch;
  font-size: clamp(17px, 1.3vw, 19px);
  line-height: 1.62;
  p {
    margin: 0 0 1em;
  }
  a {
    color: inherit;
    font-weight: 700;
    text-decoration-thickness: 2px;
    text-underline-offset: 3px;
  }
`;

const ArtWithRing = styled.figure<{ $tilt?: number }>`
  position: relative;
  margin: 0;
  justify-self: center;
  width: min(460px, 82vw, 62vh);
  img {
    display: block;
    width: 100%;
    height: auto;
    border-radius: 46% 54% 50% 50% / 52% 48% 52% 48%;
    transform: rotate(${(p) => p.$tilt ?? -4}deg);
    transition: transform 380ms ${EASE_OUT}, border-radius 380ms ${EASE_OUT};
  }
  .ring {
    position: absolute;
    inset: -7% -9%;
    width: 118%;
    height: 114%;
    pointer-events: none;
    transition: transform 420ms ${EASE_OUT};
  }
  ${hover(css`
    &:hover img {
      transform: rotate(0deg) scale(1.03);
      border-radius: 50%;
    }
    &:hover .ring {
      transform: rotate(-8deg) scale(1.02);
    }
  `)}
`;

const Screen = styled.div<{ $small?: boolean }>`
  position: relative;
  width: ${(p) => (p.$small ? '100%' : 'min(1240px, 94vw)')};
  margin: ${(p) => (p.$small ? '0' : '20px auto 10px')};
  .video {
    display: block;
    width: 100%;
    aspect-ratio: 16 / 9;
    object-fit: cover;
    background: ${C.ink};
    border: 4px solid ${C.ink};
    border-radius: 28px 36px 24px 40px / 34px 26px 38px 28px;
    transform: rotate(-1deg);
    transition: transform 380ms ${EASE_OUT};
  }
  ${hover(css`
    &:hover .video {
      transform: rotate(0deg) scale(1.01);
    }
  `)}
  .b1 {
    position: absolute;
    top: -46px;
    left: -26px;
    z-index: 1;
    transform: rotate(-14deg);
  }
  .b2 {
    position: absolute;
    right: -18px;
    bottom: -30px;
    z-index: 1;
    transform: rotate(18deg);
  }
  @media (max-width: 600px) {
    .b1,
    .b2 {
      display: none;
    }
  }
`;

const spin = keyframes`
  to { transform: translate(-50%, -50%) scaleY(0.36) rotate(360deg); }
`;

const coinFlip = keyframes`
  0% { transform: rotateY(0deg); }
  100% { transform: rotateY(360deg); }
`;

/**
 * The black hole fills the whole section, Zeus on his lily pad at the right. The words sit in
 * a dark comic bubble on the calm water at the left, its tail pointing at him, as if he told
 * the story. A golden glow turns slowly over the whirlpool.
 */
const Dollarize = styled.section`
  position: relative;
  height: max(620px, 100vh);
  overflow: hidden;
  background: #0b132b;
  .bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: 62% center;
    transition: transform 1600ms ${EASE_OUT};
  }
  ${hover(css`
    &:hover .bg {
      transform: scale(1.035);
    }
  `)}
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    background: linear-gradient(90deg, rgba(11, 19, 43, 0.72) 0%, rgba(11, 19, 43, 0.35) 34%, rgba(11, 19, 43, 0) 56%),
      linear-gradient(180deg, rgba(11, 19, 43, 0.45) 0%, rgba(11, 19, 43, 0) 18%, rgba(11, 19, 43, 0) 82%, rgba(11, 19, 43, 0.5) 100%);
    pointer-events: none;
  }
  .swirl {
    position: absolute;
    left: 71%;
    top: 85%;
    z-index: 1;
    width: 38vw;
    height: 38vw;
    transform: translate(-50%, -50%) scaleY(0.36);
    border-radius: 50%;
    background: conic-gradient(from 0deg, rgba(255, 214, 60, 0) 0deg, rgba(255, 236, 140, 0.55) 40deg, rgba(255, 214, 60, 0) 90deg,
      rgba(255, 214, 60, 0) 180deg, rgba(255, 236, 140, 0.45) 220deg, rgba(255, 214, 60, 0) 270deg);
    -webkit-mask-image: radial-gradient(circle, transparent 28%, #000 40%, #000 62%, transparent 72%);
    mask-image: radial-gradient(circle, transparent 28%, #000 40%, #000 62%, transparent 72%);
    mix-blend-mode: screen;
    animation: ${spin} 14s linear infinite;
    pointer-events: none;
  }
  .inner {
    position: relative;
    z-index: 2;
    height: 100%;
    max-width: 1320px;
    margin: 0 auto;
    padding: 0 clamp(24px, 4vw, 56px);
    display: flex;
    align-items: center;
  }
  .bubble {
    position: relative;
    width: min(540px, 44vw);
    padding: clamp(60px, 9vh, 84px) clamp(26px, 2.6vw, 40px) clamp(26px, 4vh, 38px);
    background: rgba(11, 19, 43, 0.84);
    border: 4px solid ${C.white};
    border-radius: 34px 46px 38px 52px / 44px 34px 52px 38px;
    box-shadow: 6px 8px 0 rgba(0, 0, 0, 0.45);
    color: ${C.white};
    transition: transform 400ms ${EASE_OUT};
  }
  /* the tail points at Zeus */
  .bubble::after {
    content: '';
    position: absolute;
    right: -46px;
    top: 46%;
    width: 0;
    height: 0;
    border-top: 18px solid transparent;
    border-bottom: 18px solid transparent;
    border-left: 48px solid ${C.white};
    transform: rotate(14deg);
  }
  .bubble::before {
    content: '';
    position: absolute;
    right: -36px;
    top: calc(46% + 7px);
    z-index: 1;
    width: 0;
    height: 0;
    border-top: 11px solid transparent;
    border-bottom: 11px solid transparent;
    border-left: 36px solid rgba(11, 19, 43, 0.95);
    transform: rotate(14deg);
  }
  ${hover(css`
    .bubble:hover {
      transform: rotate(-0.6deg) translateY(-4px);
    }
  `)}
  h2 {
    position: absolute;
    top: 0;
    left: 22px;
    right: 10px;
    margin: 0;
    transform: translateY(-58%);
  }
  p {
    margin: 0 0 clamp(8px, 1.6vh, 14px);
    font-family: 'Ubuntu', sans-serif;
    font-weight: 500;
    font-size: clamp(15px, min(1.25vw, 2.1vh), 17.5px);
    line-height: 1.55;
  }
  .links {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 14px 26px;
    margin-top: clamp(14px, 2.4vh, 22px);
  }
  .mint {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    padding: 12px 26px 12px 14px;
    border: 4px solid ${C.ink};
    border-radius: 999px;
    background: ${C.yellow};
    color: ${C.ink};
    font-family: 'GROBOLD', sans-serif;
    font-size: 20px;
    text-decoration: none;
    box-shadow: 4px 5px 0 ${C.ink};
    transition: transform 200ms ${EASE_OUT}, box-shadow 200ms ${EASE_OUT};
  }
  .coin {
    display: inline-grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border: 3px solid ${C.ink};
    border-radius: 50%;
    background: radial-gradient(circle at 35% 30%, #fff6b0, #f5c518 60%, #c99700);
    font-size: 18px;
  }
  ${hover(css`
    .mint:hover {
      transform: translateY(-3px);
      box-shadow: 5px 8px 0 ${C.ink};
    }
    .mint:hover .coin {
      animation: ${coinFlip} 700ms ${EASE_OUT};
    }
    .how:hover {
      color: ${C.yellow};
      text-decoration-style: wavy;
    }
  `)}
  .mint:active {
    transform: translate(3px, 4px);
    box-shadow: 1px 1px 0 ${C.ink};
  }
  .how {
    color: ${C.white};
    font-family: 'GROBOLD', sans-serif;
    font-size: 17px;
    text-decoration: underline;
    text-decoration-thickness: 3px;
    text-underline-offset: 5px;
    transition: color 200ms;
  }
  @media (prefers-reduced-motion: reduce) {
    .swirl { animation: none; }
    .bg { transition: none; }
  }
  @media (max-width: 900px) {
    height: auto;
    .inner { padding: 120px 16px 260px; }
    .bubble { width: 100%; }
    .bubble::before, .bubble::after { display: none; }
  }
`;

const Nfts = styled.section`
  max-width: 1240px;
  margin: 0 auto;
  padding: clamp(24px, 5vh, 90px) clamp(16px, 4vw, 40px);
  .head {
    display: grid;
    justify-items: center;
    text-align: center;
    margin-bottom: 40px;
  }
  .head > div {
    max-width: 60ch;
  }
  h2 {
    margin-bottom: 18px;
  }
  .how {
    position: relative;
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    font-weight: 700;
    color: ${C.ink};
    text-decoration: none;
  }
  .frames {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: clamp(16px, 2.6vw, 36px);
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .frames li {
    transition: transform 280ms ${EASE_OUT};
  }
  .frames a {
    display: block;
    border-radius: 28px 34px 26px 38px / 34px 26px 38px 28px;
  }
  .frames img {
    display: block;
    width: min(100%, 21vh);
    margin: 0 auto;
    aspect-ratio: 1;
    object-fit: cover;
    border: 3px solid ${C.ink};
    border-radius: inherit;
  }
  ${hover(css`
    .frames li:hover {
      z-index: 2;
      transform: rotate(0deg) scale(1.1) !important;
    }
  `)}
  @media (max-width: 900px) {
    .frames {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
`;

const Descent = styled.div`
  background-image: ${PRINT}, linear-gradient(180deg, ${C.light} 0%, ${C.sky} 30%, ${C.blue} 100%);
  background-size: 18px 18px, 100% 100%;
`;

const Block = styled.section`
  max-width: 1240px;
  margin: 0 auto;
  padding: clamp(24px, 5vh, 90px) clamp(16px, 4vw, 40px);
  .verify {
    max-width: 640px;
    margin: 26px auto 46px;
    text-align: center;
    font-weight: 500;
    font-size: 17px;
  }
  .donut {
    display: flex;
    justify-content: center;
  }
  .sub {
    max-width: 620px;
    margin: -8px auto 36px;
    font-size: 17px;
    line-height: 1.55;
  }
`;

const Path = styled.ol`
  position: relative;
  max-width: 1040px;
  margin: 10px auto 0;
  padding: 0;
  list-style: none;
  ol {
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .fold {
    position: relative;
    z-index: 1;
    padding: 22px 0;
  }
  .fold summary {
    position: relative;
    flex-direction: column;
    align-items: flex-end;
    gap: 10px;
    width: calc(50% - 48px);
    text-align: right;
  }
  .fold-dot {
    position: absolute;
    top: 8px;
    right: -61px;
    width: 26px;
    height: 26px;
    background: ${C.white};
    border: 4px dashed ${C.ink};
    border-radius: 50%;
  }
  .fold-toggle .opened,
  .fold[open] .fold-toggle .closed {
    display: none;
  }
  .fold[open] .fold-toggle .opened {
    display: inline;
  }
  .fold[open] .fold-dot {
    background: ${C.yellow};
    border-style: solid;
  }
  @media (max-width: 760px) {
    .fold summary {
      width: auto;
      align-items: flex-start;
      padding-left: 52px;
      text-align: left;
    }
    .fold-dot {
      left: 7px;
      right: auto;
    }
  }
  .fold[open] summary {
    margin-bottom: 10px;
  }
  .trail {
    position: absolute;
    top: 0;
    bottom: 0;
    left: calc(50% - 20px);
    width: 40px;
    height: 100%;
  }
  @media (max-width: 760px) {
    .trail {
      left: 0;
    }
  }
`;

const Stop = styled.li<{ $side: 'left' | 'right'; $status: string }>`
  position: relative;
  display: flex;
  justify-content: ${(p) => (p.$side === 'left' ? 'flex-start' : 'flex-end')};
  padding: 18px 0;
  .dot {
    position: absolute;
    top: 30px;
    left: calc(50% - 13px);
    width: 26px;
    height: 26px;
    background: ${(p) => (p.$status === 'planned' ? C.white : C.yellow)};
    border: 4px solid ${C.ink};
    border-radius: 50% 46% 52% 48%;
    transition: transform 260ms ${EASE_OUT};
  }
  .what {
    width: calc(50% - 48px);
    ${(p) => (p.$side === 'left' ? 'text-align: right;' : '')}
  }
  .pill {
    display: inline-block;
    margin-bottom: 6px;
    padding: 3px 12px;
    font-family: 'GROBOLD', sans-serif;
    font-size: 14px;
    background: ${C.yellow};
    border: 2.5px solid ${C.ink};
    border-radius: 999px;
    transform: rotate(-3deg);
  }
  h3 {
    margin: 0 0 6px;
    font-family: 'GROBOLD', sans-serif;
    font-weight: 400;
    font-size: clamp(19px, 1.7vw, 24px);
    line-height: 1.2;
  }
  h3 span {
    display: block;
  }
  ${(p) =>
    p.$status === 'in-progress' &&
    css`
      .dot {
        width: 40px;
        height: 40px;
        left: calc(50% - 20px);
        top: 24px;
        background: ${C.pink};
      }
      h3 {
        font-size: clamp(24px, 2.2vw, 32px);
      }
    `}
  p {
    margin: 0;
    font-size: 17px;
    line-height: 1.55;
  }
  a {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    color: inherit;
    font-weight: 700;
  }
  ${hover(css`
    &:hover .dot {
      transform: scale(1.35) rotate(20deg);
    }
  `)}
  @media (max-width: 760px) {
    justify-content: flex-start;
    .dot {
      left: ${(p) => (p.$status === 'in-progress' ? '0px' : '7px')};
    }
    .what {
      width: auto;
      padding-left: 52px;
      text-align: left;
    }
  }
`;

const bob = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(-6px); }
`;

const Swamp = styled.section`
  background-color: ${C.blue};
  padding-top: 40px;
  .memes-title {
    position: relative;
    z-index: 2;
    margin-bottom: -40px;
  }
  .stage {
    position: relative;
  }
  .scene {
    display: block;
    width: 100%;
    height: min(calc(100vw * 941 / 1672), calc(100vh - 170px));
    object-fit: cover;
    -webkit-mask-image: linear-gradient(180deg, transparent 0%, #000 14%, #000 88%, transparent 100%);
    mask-image: linear-gradient(180deg, transparent 0%, #000 14%, #000 88%, transparent 100%);
  }
  .pads {
    position: absolute;
    left: 7%;
    right: 7%;
    top: 24%;
    bottom: 8%;
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    align-content: space-evenly;
    align-items: center;
    gap: 1.2vw 2.4vw;
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .pads li {
    animation: ${bob} 3.2s ease-in-out infinite alternate;
  }
  .pads li:nth-child(odd) {
    margin-top: 2.4vw;
  }
  .bub {
    position: relative;
    transform: rotate(-3deg);
    transition: transform 280ms ${EASE_OUT};
  }
  .pads li:nth-child(odd) .bub {
    transform: rotate(3deg);
  }
  .pads img {
    cursor: pointer;
    display: block;
    width: min(100%, 15vh);
    margin: 0 auto;
    aspect-ratio: 1;
    object-fit: cover;
    border: 3px solid ${C.ink};
    border-radius: 50% 46% 52% 48% / 48% 52% 46% 54%;
    transition: border-radius 280ms ${EASE_OUT};
  }
  /* the X mark sits inside the picture and shows when the bubble opens */
  .share-x {
    position: absolute;
    right: 9%;
    bottom: 9%;
    display: grid;
    place-items: center;
    width: 22px;
    height: 22px;
    padding: 0;
    border: 2px solid ${C.white};
    border-radius: 50%;
    background: ${C.ink};
    color: ${C.white};
    cursor: pointer;
    opacity: 0;
    transform: scale(0.6);
    transition: opacity 180ms ${EASE_OUT}, transform 220ms ${EASE_OUT}, background 160ms;
  }
  .share-x svg {
    width: 11px;
    height: 11px;
  }
  @media (hover: none) {
    .share-x { opacity: 1; transform: none; }
  }
  .share-x:focus-visible {
    opacity: 1;
    transform: scale(1);
    outline: 2px dashed ${C.yellow};
    outline-offset: 2px;
  }
  ${hover(css`
    .pads li:hover {
      position: relative;
      z-index: 3;
      animation-play-state: paused;
    }
    .pads li:hover .bub,
    .pads li:nth-child(odd):hover .bub {
      transform: scale(1.75) rotate(0deg);
    }
    .pads li:hover img {
      border-radius: 18px;
    }
    .pads li:hover .share-x {
      opacity: 1;
      transform: scale(1);
      transition-delay: 120ms;
    }
    .share-x:hover {
      background: #1d9bf0;
      transform: scale(1.15) !important;
    }
  `)}
  @media (max-width: 900px) {
    .memes-title {
      margin-bottom: 10px;
    }
    .pads {
      position: static;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 16px;
      padding: 24px 16px 10px;
    }
    .pads li {
      animation: none;
    }
    .pads li:nth-child(odd) {
      margin-top: 0;
    }
    .pads img {
      border-radius: 22px;
    }
  }
`;

const Shore = styled.div`
  background: ${C.blue};
`;

const Join = styled.section`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 20px 60px;
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px clamp(16px, 4vw, 40px) 60px;
  .zeus {
    width: min(560px, 90vw);
    height: auto;
    transform-origin: 50% 90%;
    transition: transform 400ms ${EASE_OUT};
  }
  ${hover(css`
    .zeus:hover {
      transform: rotate(-4deg) scale(1.03);
    }
  `)}
  > div {
    display: grid;
    justify-items: center;
    gap: 22px;
  }
`;
